<template>
    <KebabMenu
        v-if="props.filters?.length"
        icon="filter" sprite="navigation"
        :leftAuto="!locale.rtl"
        :rightAuto="locale.rtl"
        :tertiary="!Object.keys(data.values).length"
        size="size-m"
        ref="menu"
        @toggle="init"
    >
        <ul @click.stop="">
            <li v-for="(item, id) in props.filters"
                :key="id"
                class="filter-field"
            >
                <Field
                    v-if="item.type === 'string'"
                    :placeholder="t(item.name)"
                    :value="data.values[item.code]"
                    class="noerror light with-placeholder"
                    ref="refs"
                    @change="onFieldChange($event, item)"
                />

                <DateField
                    v-else-if="item.type === 'date'"
                    :autoApply="true"
                    :range="item.range"
                    :text-input="true"
                    :enableTimePicker="false"
                    :timePicker="false"
                    :placeholder="t(item.name)"
                    :teleport="true"
                    :modelValue="data.values[item.code]"
                    format="dd.MM.yyyy"
                    class="light noerror with-placeholder"
                    ref="refs"
                    @update:modelValue="onDateFieldChange($event, item)"
                />

                <AutoComplete
                    v-else-if="item.type === 'autocomplete'"
                    :placeholder="t(item.name)"

                    :optionList="data.options[item.code] || []"
                    :values="data.values[item.code] || []"
                    :maxPreviewValuesCount="1"
                    :maxValuesCount="item.multiple ? 20 : 1"
                    :useI18n="!!item.useI18n"
                    class="noerror light with-placeholder"
                    ref="refs"
                    @search="onAutoCompleteSearch($event, item)"
                    @change="onAutoCompleteChange($event, item)"
                />

                <span v-else class="name"
                    ref="refs"
                >{{ t(item.name) }}</span>
            </li>
            <li>
                <div class="buttons">
                    <Button class="secondary size-m" name="Clear"
                        @click="clear"
                    />
                    <Button class="size-m"
                        name="Accept"
                        @click="accept"
                    />
                </div>
            </li>
        </ul>

        <div class="loader" v-if="!data.ready">
            <div>
                <Loader :inside="true" class="tertiary" />
            </div>
        </div>
    </KebabMenu>
</template>

<script setup>
    import { reactive, useTemplateRef, ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import KebabMenu from '🔗/components/ui/KebabMenu.vue';
    import Field from '🔗/components/ui/Field.vue';
    import Button from '🔗/components/ui/Button.vue';
    import DateField from '🔗/components/ui/DateField.vue';
    import AutoComplete from '🔗/components/ui/AutoComplete.vue';
    import Loader from '🔗/components/ui/Loader.vue';
    import moment from 'moment';
    import { delay } from '🔗/utils/utils';
    import stores from '@/stores';

    const locale = stores.locale();

    const { t } = useI18n();
    const emit = defineEmits(['change']);
    const refs = useTemplateRef('refs');
    const menu = ref(null);

    const props = defineProps({
        filters: { type: Array, default: () => ([]) }
    });

    const data = reactive({
        options: {},
        values: {}
    });

    const onAutoCompleteSearch = async (value, item) => {
        const searchStr = value?.toLowerCase().trim();

        data.options[item.code] = await item.onSearch(searchStr);
    }

    const onFieldChange = async (e, item) => {
        data.values[item.code] = e.target.value;
    }

    const onDateFieldChange = async (values, item) => {
        if (item.range && values?.length !== 2) {
            data.values[item.code] = null
        } else {
            data.values[item.code] = values;
        }
    }

    const onAutoCompleteChange = ({ values }, item) => {
        data.values[item.code] = values?.length ? values : [];
    };

    const init = async () => {
        if (data.ready) {
            return;
        }

        await Promise.all(props.filters.filter(item => item.type === 'autocomplete').map(async (item) => onAutoCompleteSearch('', item)));

        await delay(200);

        data.ready = true;
    }

    const clear = () => {
        data.ready = false;
        data.values = {};

        refs.value?.forEach(item => {
            if (item?.field) {
                item.field.value = null;
            }
        });

        accept();
        init();
    }

    const accept = (e) => {
        let result = {};

        props.filters.forEach(filter => {
            if ([null, undefined].indexOf(data.values[filter.code]) === -1) {
                let payload = {}

                // Default values
                    if (filter.type === 'string') {
                        payload[filter.code] = data.values[filter.code]?.trim().toLowerCase();
                    }

                    if (filter.type === 'date') {
                        if (filter.range && data.values[filter.code]?.length === 2) {
                            payload[filter.code] = data.values[filter.code].map(item => moment(item).toISOString());
                        }
                        if (!filter.range && data.values[filter.code]) {
                            payload[filter.code] = moment(data.values[filter.code]).toISOString();
                        }
                    }

                    if (filter.type === 'autocomplete') {
                        if (filter.multiple && data.values[filter.code]?.length) {
                            payload[filter.code] = data.values[filter.code].map(item => item.code).join(',');
                        }

                        if (!filter.multiple && data.values[filter.code]?.length) {
                            payload[filter.code] = data.values[filter.code][0].code;
                        }
                    }

                if (filter.formatter) {
                    payload = filter.formatter(data.values[filter.code]);
                }

                if (payload && Object.keys(payload)?.length) {
                    result = { ...result, ...payload };
                }
            }
        });

        emit('change', result);

        if (e) {
            menu?.value?.close();
        }
    }
</script>

<style lang="scss" scoped>
.filter-field {
    min-width: min(100vw, 400px);
    max-width: min(100vw, 400px);
    & > div {
        width: 100%;
        flex: 1 1 100%
    }
}

.buttons {
    // margin-top: 20px;
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    justify-content: end;
    width: 100%;
}
</style>
